import React from 'react';
import { CheckCircle2, Heart, Target, Sparkles, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { aboutContent } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen bg-[#ede1c5] text-stone-900">
      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.pexels.com/photos/35898529/pexels-photo-35898529.jpeg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede1c5]/85 via-[#ede1c5]/55 to-[#ede1c5]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-amber-400/12 blur-[140px]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-amber-700/90 tracking-[0.3em] text-[11px] uppercase mb-6">
            <span className="h-px w-12 bg-amber-600/60" />
            <Sparkles size={14} />
            <span>Our Story</span>
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] text-stone-900 max-w-3xl">
            Preserving the<br />
            <span className="italic text-amber-800">Theravāda</span> tradition.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-stone-700 leading-relaxed">
            A community devoted to the oldest surviving lineage of the Buddha's teachings —
            here in the heart of Alabama.
          </p>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3 flex items-center gap-3">
              <Target size={14} /> Mission
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-6 leading-tight">
              Why we exist
            </h2>
            <p className="text-stone-700 text-lg leading-relaxed mb-12">
              {aboutContent.mission}
            </p>

            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3 flex items-center gap-3">
              <Heart size={14} /> Vision
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-6 leading-tight">
              Where we look
            </h2>
            <p className="text-stone-700 text-lg leading-relaxed">{aboutContent.vision}</p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-amber-400/10 blur-3xl rounded-full" />
            <img
              src="https://images.unsplash.com/photo-1558980971-97f50d0fed00"
              alt="Buddha statue on hilltop"
              className="relative rounded-2xl shadow-2xl w-full h-[520px] object-cover border border-amber-500/40"
            />
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              The Practice
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Our core values
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {aboutContent.values.map((value, index) => (
              <div
                key={index}
                className="p-7 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:border-amber-500/40 hover:bg-[#fbf5e3]/90 transition-all flex items-start gap-4"
              >
                <CheckCircle2 className="text-amber-700/90 flex-shrink-0 mt-1" size={20} />
                <p className="text-stone-800 leading-relaxed">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              For You
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">What we offer</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'For Individuals',
                items: [
                  'Guided meditation sessions for all experience levels',
                  'Personal spiritual counseling and guidance',
                  'Weekly Dharma teachings and discussions',
                  'Opportunities for Dāna (generosity) practice',
                ],
              },
              {
                title: 'For Families & Community',
                items: [
                  'Sunday school for children with Buddhist values',
                  'Monthly community gatherings and shared meals',
                  'Special ceremony celebrations (Vesak, Poya days)',
                  'Community service and outreach programs',
                ],
              },
            ].map((block) => (
              <div
                key={block.title}
                className="p-10 rounded-2xl border border-amber-500/40 bg-gradient-to-b from-[#fbf5e3]/90 to-[#f3e7c4]/80 hover:border-amber-500/40 transition-all"
              >
                <h3 className="font-serif-display text-2xl text-stone-900 mb-6">{block.title}</h3>
                <ul className="space-y-3">
                  {block.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-3 text-stone-700">
                      <span className="mt-2 w-1 h-1 rounded-full bg-amber-600 flex-shrink-0" />
                      <span className="leading-relaxed">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRADITION CTA */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/36481732/pexels-photo-36481732.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#ede1c5]/80 via-amber-50/70 to-[#fef3c7]/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-4">
            The Lineage
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl text-stone-900 leading-tight mb-6">
            Theravāda — <span className="italic text-amber-800">the teaching of the elders.</span>
          </h2>
          <p className="text-lg text-stone-700 leading-relaxed mb-10 max-w-2xl mx-auto">
            We follow the oldest surviving branch of Buddhism, preserving the original teachings of
            the Buddha as recorded in the Pāli Canon — meditation, ethical conduct, and the
            cultivation of wisdom.
          </p>
          <Link
            to="/teachings"
            className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-600 text-black px-8 py-4 rounded-full text-sm tracking-wide"
          >
            Explore Teachings <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
